import { Grid } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { epochToFormatDate } from "../../../../../app/common";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import FormSection from "../../../../../common/components/functional/formSection/FormSection";
import FormTable, {
  DataItem,
} from "../../../../../common/components/functional/formTable/FormTable";
import OverlaySpinner from "../../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import { isoFormat, today } from "../../../../../common/helpers/formatDateTime";
import { EventPayload } from "../../../../../common/types/Event";
import { fetchTesPriceList } from "../../../../settings/settingsActions";
import {
  AssignmentById,
  AssignmentSupplementPriceOverridesOverviewItem,
  AssignmentSupplementPriceOverridesOverviewQuery,
} from "../../../../talents/talentAPI";
import { fetchAssignmentSupplementPriceOverridesOverview } from "../../../../talents/talentsSlice";
import {
  AssignmentSupplementPriceOverridesChangedV2Payload,
  PriceValueType,
  TesPricingOverrideOptions,
} from "../../../../tesPricingOverride/tesPricingOverrideAPI";
import { ContentKey } from "../../../TalentDetails";
import { adjustForMultiplier } from "../helpers";
import AssignmentSupplementsPriceOverridesDialog from "./AssignmentSupplementsPriceOverridesDialog";
import {
  AssignmentSupplementPriceOverridesRowData,
  mapAssignmentSupplementPriceOverridesRows,
} from "./assignmentSupplementsPriceOverridesMapper";

interface Props {
  assignment: AssignmentById;
  canEdit: boolean;
}

type DialogState = {
  open: boolean;
  mode: "add" | "edit";
  selectedRow: AssignmentSupplementPriceOverridesRowData | null;
};

const AssignmentSupplementsPriceOverrides = ({
  assignment,
  canEdit,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const translatedSupplementNames = useAppSelector(
    (state) => state.localized.supplement,
  );

  const [tesPricingOverrideOptions, setTesPricingOverrideOptions] =
    useState<TesPricingOverrideOptions>({});

  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState<AssignmentSupplementPriceOverridesRowData[]>(
    [],
  );
  const [dialogState, setDialogState] = useState<DialogState>({
    open: false,
    mode: "add",
    selectedRow: null,
  });

  // TODO: used also in TesPricingOverride
  // Consider moving this to a separate hook
  useEffect(() => {
    if (!assignment.details.tesId || !assignment.details.start) return;

    dispatch(
      fetchTesPriceList({
        tesId: assignment.details.tesId,
        date: epochToFormatDate(assignment.details.start, "yyyy-MM-dd"),
      }),
    ).then((action) => {
      if (isFulfilled(action)) {
        const options: TesPricingOverrideOptions = action.payload.reduce(
          (acc, item) => {
            Object.entries(item.prices).forEach(([key, priceDetails]) => {
              if (priceDetails.unitValueOverrideAllowed) {
                acc[key] = {
                  unit:
                    (priceDetails.unit as PriceValueType) || priceDetails.unit,
                  unitValue: adjustForMultiplier(
                    priceDetails.unitValue,
                    priceDetails.unit,
                  ),
                };
              }
            });
            return acc;
          },
          {} as TesPricingOverrideOptions,
        );
        setTesPricingOverrideOptions(options);
      }
    });
  }, [assignment.details.tesId, assignment.details.start, dispatch]);

  useEffect(() => {
    if (!assignment.assignmentId) {
      setRows([]);
      return;
    }

    setRows([]);
    setIsLoading(true);

    const queryParams: AssignmentSupplementPriceOverridesOverviewQuery = {
      assignmentId: assignment.assignmentId,
      date: today(isoFormat),
    };

    dispatch(fetchAssignmentSupplementPriceOverridesOverview(queryParams)).then(
      (action) => {
        if (isFulfilled(action)) {
          setRows(action.payload);
        }
        setIsLoading(false);
      },
    );
  }, [assignment.assignmentId, dispatch]);

  const headers = useMemo(
    () => [
      { text: t("tesPricingOverride:selectItem") },
      { text: t("tesPricingOverride:valueOrStatus") },
    ],
    [t],
  );

  const tableData: DataItem[] = useMemo(
    () =>
      rows.map((row) => ({
        id: row.code,
        values: {
          code: translatedSupplementNames?.[row.code] || row.code,
          valueOrStatus: row.valueOrStatusText,
        },
        isEditable: true,
      })),
    [rows, translatedSupplementNames],
  );

  const handleOpenEditModal = (_content: ContentKey, id?: string) => {
    const selectedRow =
      id !== undefined ? rows.find((row) => row.code === id) || null : null;

    setDialogState({
      open: true,
      mode: id ? "edit" : "add",
      selectedRow,
    });
  };

  const handleCloseDialog = () =>
    setDialogState({
      open: false,
      mode: "add",
      selectedRow: null,
    });

  const handleSaved = (
    events: EventPayload<AssignmentSupplementPriceOverridesChangedV2Payload>[],
  ) => {
    const event = events.find((e) => e.name === "assgnSupPrcChgV2");
    if (!event) return;

    const { code, schedule } = event.payload;
    const todayStr = today(isoFormat);
    const values = schedule.values;
    const count = values.length;
    const currentValue =
      [...values].reverse().find((v) => v.date <= todayStr)?.value ?? null;
    const next = values.find((v) => v.date > todayStr);

    const updatedItem: AssignmentSupplementPriceOverridesOverviewItem = {
      code,
      count,
      currentValue,
      nextDate: next?.date ?? null,
      nextValue: next?.value ?? null,
      unit: schedule.unit,
    };
    const updatedRow = mapAssignmentSupplementPriceOverridesRows([
      updatedItem,
    ])[0];

    setRows((prev) => {
      if (count === 0) return prev.filter((r) => r.code !== code);
      const idx = prev.findIndex((r) => r.code === code);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = updatedRow;
        return updated;
      }
      return [...prev, updatedRow];
    });
  };

  return (
    <>
      <FormSection
        title={t("tesPricingOverride:tesPricingOverrideTitle")}
        noBorder
      >
        <Grid item xs={2} />
        <Grid item xs={10} sx={{ position: "relative" }}>
          <OverlaySpinner isLoading={isLoading} />
          <FormTable
            data={tableData}
            headers={headers}
            handleOpenEditModal={handleOpenEditModal}
            addButtonLabel={t("tesPricingOverride:addTesPricingOverrideItem")}
            disableEditRow={!canEdit || isLoading}
            emptyStateText={{
              show: !isLoading && rows.length === 0,
              text: t("talentProfile:noSupplementsToShow"),
            }}
          />
        </Grid>
      </FormSection>
      <AssignmentSupplementsPriceOverridesDialog
        open={dialogState.open}
        mode={dialogState.mode}
        selectedRow={dialogState.selectedRow}
        supplementLabel={
          dialogState.selectedRow
            ? translatedSupplementNames?.[dialogState.selectedRow.code] ||
              dialogState.selectedRow.code
            : undefined
        }
        assignment={assignment}
        existingCodes={rows.map((r) => r.code)}
        tesPricingOverrideOptions={tesPricingOverrideOptions}
        onClose={handleCloseDialog}
        onSaved={handleSaved}
      />
    </>
  );
};

export default AssignmentSupplementsPriceOverrides;
